import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/site/Form";
import { useAuth } from "@/lib/auth";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { SettingsPanel } from "./IntegrationStatus";

const ChangePassword = () => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const go = async () => {
    try { await api.post("/auth/change-password", f); toast.success("Password changed"); setF({ current_password: "", new_password: "" }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <SettingsPanel title="Change your password" testid="change-password-panel">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Current password" htmlFor="cp"><input id="cp" type="password" autoComplete="current-password" className="field-input" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="current-password-input" /></Field>
        <Field label="New password (10+ characters)" htmlFor="np"><input id="np" type="password" autoComplete="new-password" className="field-input" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="new-password-input" /></Field>
      </div>
      <Button onClick={go} disabled={f.new_password.length < 9} data-testid="change-password-button">Change password</Button>
    </SettingsPanel>
  );
};

export const UsersSettings = ({ owner }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ email: "", name: "", role: "staff" });
  const load = useCallback(() => api.get("/dealer/users").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const invite = async () => {
    try {
      const { data } = await api.post("/dealer/users", f);
      data.email_status === "sent" ? toast.success("Invitation emailed") : toast.error(`User created but invitation email not sent (${data.error}). They can use 'Forgotten your password?' once email works.`);
      setF({ email: "", name: "", role: "staff" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this user's access?")) return;
    try { await api.delete(`/dealer/users/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5">
      <SettingsPanel title="Dashboard users" testid="users-panel">
        <ul className="divide-y divide-border text-sm">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid="user-row">
              <span><strong>{r.name}</strong> · {r.email} · {r.role}{!r.active ? " · removed" : ""}<br /><span className="text-xs text-slate-500">Last sign in: {r.last_login_at ? fmtDateTime(r.last_login_at) : "never"}</span></span>
              {owner && r.active && r.id !== user.id && <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid="remove-user-button">Remove access</Button>}
            </li>
          ))}
        </ul>
        {owner && (
          <div className="grid gap-3 border-t border-border pt-4 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end">
            <Field label="Name" htmlFor="iu-name"><input id="iu-name" className="field-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name-input" /></Field>
            <Field label="Email" htmlFor="iu-email"><input id="iu-email" type="email" className="field-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email-input" /></Field>
            <select aria-label="Role" className="field-input w-auto" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="invite-role-select"><option value="staff">Staff</option><option value="owner">Owner</option></select>
            <Button onClick={invite} disabled={!f.email || !f.name} data-testid="invite-user-button">Send invitation</Button>
          </div>
        )}
        <p className="text-xs text-slate-500">Invited users choose their own password from a single-use emailed link. Staff can manage enquiries; only owners can change settings or delete records.</p>
      </SettingsPanel>
      <ChangePassword />
    </div>
  );
};
